import { TFunction } from 'i18next';
import isEmpty from 'lodash-es/isEmpty';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactRouterPrompt from 'react-router-prompt';
import styled from 'styled-components';

import { AtiraTabs } from '../../components/AtiraTabs';
import { Flex } from '../../components/Flex';
import { UserHeader } from '../../components/UserHeader';
import { WarningModal } from '../../components/WarningModal';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import i18n, { AppLangs } from '../../i18n';
import { AtiraLink } from '../../model/atira-link/AtiraLink';
import { CreateAtiraLinkDto } from '../../model/atira-link/dto/CreateAtiraLinkDto';
import { AtiraLinkStylesBackgroundMode } from '../../model/atira-link/types/AtiraLinkStylesBackgroundMode.enum';
import { AtiraLinkThemes } from '../../model/atira-link/types/AtiraLinkThemes.enum';
import { atiraLinkSliceSelectors } from '../../redux/atira-link/atira-link.selector';
import { useAppSelector } from '../../redux/store';
import { userSliceSelectors } from '../../redux/user/user.selector';
import { Breakpoints } from '../../theme/Breakpoints';
import { Rounded } from '../../theme/Rounded';
import { Spacing } from '../../theme/Spacing';
import { AtiraLinkPreviewPhoneWithContext } from './components/AtiraLinkPreviewPhoneWithContext';
import { AtiraLinkContentTab } from './tabs/AtiraLinkContentTab';
import { AtiraLinkDesignTab } from './tabs/AtiraLinkDesignTab';
import { AtiraLinkTemplatesTab } from './tabs/AtiraLinkTemplatesTab';

enum TabKeys {
  CONTENT = 'CONTENT',
  TEMPLATES = 'TEMPLATES',
  DESIGN = 'DESIGN',
}

const getTabs = (t: TFunction) => [
  {
    key: TabKeys.CONTENT,
    label: t('common.content'),
    children: <AtiraLinkContentTab />,
  },
  {
    key: TabKeys.TEMPLATES,
    label: t('common.templates'),
    children: <AtiraLinkTemplatesTab />,
  },
  {
    key: TabKeys.DESIGN,
    label: t('common.design'),
    children: <AtiraLinkDesignTab />,
  },
];

const HeaderPhoneWrapper = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.m};
  align-items: flex-end;
  position: sticky;
  top: ${Spacing.s};
  height: min-content;
  width: 0;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    padding-right: ${i18n.language === AppLangs.AR ? '0' : Spacing.m};
    padding-left: ${i18n.language === AppLangs.AR ? Spacing.m : '0'};
    margin-top: ${Spacing.s};
    width: 100%;
    flex: 1;
  }
`;

const HeaderWrapper = styled(Flex)`
  display: none;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    display: flex;
  }
`;

const TabsWrapper = styled(Flex)`
  width: 100%;
  flex-direction: column;

  .ant-tabs-nav {
    flex-direction: column;
  }

  .ant-tabs-nav-wrap {
    width: 100%;
  }

  .ant-tabs-extra-content {
    align-self: flex-start;
  }

  .ant-tabs-extra-content > div {
    width: 15rem;
  }

  .ant-select-selector {
    border-radius: ${Rounded.lg};
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    padding-bottom: 0;

    .ant-tabs-nav {
      flex-direction: row;
    }

    .ant-tabs-nav-wrap {
      width: fit-content;
    }

    .ant-tabs-extra-content {
      height: calc(100% - ${Spacing.xl});
      align-self: center;
    }

    .ant-tabs-extra-content > div {
      height: 100%;
    }
  }
`;

export const AtiraLinkRoute: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  useDocumentTitle(
    `${t('app.name').toUpperCase()} - ${t('side_menu.my_link')}`,
  );

  const loggedInUser = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const selectedThemeFromTemplates = localStorage.getItem(
    'selectedThemeFromTemplates',
  );
  const selectedAtiraLink = useAppSelector(
    atiraLinkSliceSelectors.selectSelectedAtiraLink,
  );

  const formMethods = useForm<CreateAtiraLinkDto>({ mode: 'all' });

  const contentTabHasError = () => {
    const {
      errors: { theme, styles, ...restErrors },
    } = formMethods.formState;

    return !isEmpty(restErrors);
  };

  const themesTabHasError = () => {
    const {
      errors: { theme },
    } = formMethods.formState;

    return !isEmpty(theme);
  };

  const designTabHasError = () => {
    const {
      errors: { styles },
    } = formMethods.formState;

    return !isEmpty(styles);
  };

  useEffect(() => {
    const data = selectedAtiraLink || ({} as AtiraLink);

    const {
      title = '',
      email = { label: '', value: '' },
      phone = { label: '', value: '' },
      website = { label: '', value: '' },
      map = { label: '', value: '' },
      linkName = '',
      bio = '',
      customLinks = [],
      image = '',
      socials = [],
      embed = undefined,
      styles = {
        background: {
          color: undefined,
          postion: undefined,
          size: undefined,
          backgroundMode: AtiraLinkStylesBackgroundMode.COLOR,
        },
        backgroundImage: null,
        button: {
          backgroundColor: undefined,
          borderColor: undefined,
          borderStyle: undefined,
          borderWidth: undefined,
          borderRadius: undefined,
        },
        image: {
          borderColor: undefined,
          borderStyle: undefined,
          borderWidth: undefined,
          borderRadius: undefined,
        },
      },
      theme,
    } = data;

    formMethods.reset({
      title,
      email,
      phone,
      website,
      map,
      userId: loggedInUser._id,
      theme:
        (selectedThemeFromTemplates as AtiraLinkThemes) ||
        theme ||
        AtiraLinkThemes.BASIC,
      linkName,
      bio,
      customLinks,
      image,
      socials,
      embed,
      styles: {
        ...styles,
        background: {
          ...styles?.background,
          // eslint-disable-next-line no-nested-ternary
          backgroundMode: styles?.background?.backgroundMode
            ? styles.background.backgroundMode
            : styles?.backgroundImage?.length
              ? AtiraLinkStylesBackgroundMode.IMAGE
              : AtiraLinkStylesBackgroundMode.COLOR,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAtiraLink?._id]);

  return (
    <Flex flex={1} position="relative">
      <FormProvider {...formMethods}>
        <TabsWrapper>
          <AtiraTabs
            tabErrors={{
              [TabKeys.CONTENT]: contentTabHasError(),
              [TabKeys.TEMPLATES]: themesTabHasError(),
              [TabKeys.DESIGN]: designTabHasError(),
            }}
            items={getTabs(t)}
            showNavigationButtons
          />
        </TabsWrapper>

        <HeaderPhoneWrapper>
          <HeaderWrapper>
            <UserHeader />
          </HeaderWrapper>

          <AtiraLinkPreviewPhoneWithContext
            values={formMethods.watch()}
            loading={loading}
            setLoading={setLoading}
            showActions
          />
        </HeaderPhoneWrapper>

        <ReactRouterPrompt when={formMethods.formState.isDirty}>
          {({ isActive, onConfirm, onCancel }) =>
            isActive ? (
              <WarningModal
                onConfirm={onConfirm}
                onClose={onCancel}
                open
                title={t('common.unsaved-changes-modal.title')}
                description={t('common.unsaved-changes-modal.description')}
                confirmButtonText={t('common.unsaved-changes-modal.confirm')}
                closeButtonText={t('common.unsaved-changes-modal.cancel')}
              />
            ) : null
          }
        </ReactRouterPrompt>
      </FormProvider>
    </Flex>
  );
};
